<script setup lang="ts">
import SearchFormContent from '@/components/Every24HoursReportPreviewParts/SearchFormContent.vue'; 
import { getDateRangeDays } from '@/services/functions';
import { onMounted, reactive, ref, Ref, computed, watch } from 'vue';
import i18n from '@/lang/i18n';
import '@vuepic/vue-datepicker/dist/main.css';
import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";
import { getDatetimeWithoutSeconds } from "@/services/functions";

const MAX_FILTER_DAYS = 21;

const emits = defineEmits(['search', 'clear'])

const props = withDefaults(
  defineProps<{
    loading: boolean
    isOperationOffice: boolean
    isSubManagementOffice: boolean
    isManagementOffice: boolean
    isDepartment: boolean
    defaultBackDays: number
  }>(),
  {
    loading: false,
    isOperationOffice: false,
    isSubManagementOffice: false,
    isManagementOffice: false,
    isDepartment: false,
    defaultBackDays: 1
  }
)

const form = reactive({
  managementOffice: undefined,
  operationOffice: undefined,
  group: undefined,
  department: undefined,
  vehicle: undefined,
  driver: undefined,
  selectDateType: 'leaving',
  dateRange: [
    new Date(),
    new Date()
  ] as Date[],
})
const hide: Ref<boolean> = ref(false)
const errors: Ref<{ [key: string]: string[] }> = ref({})


const searchBtnLabel = computed(() => {
  return props.loading ? i18n.global.t('pages.common.button.loading') : i18n.global.t('pages.common.button.exec');
})

const searchAreaClass = computed(() => {
  return hide.value ? 'search-area-hide p-4' : 'search-area p-4';
})

const title = computed(() => {
  return hide.value ? '' : i18n.global.t('pages.common.filter.title');
})

const managementOfficeClearable = computed(() => {
  return props.isManagementOffice;
})

const operationOfficeClearable = computed(() => {
  return props.isManagementOffice || props.isSubManagementOffice;
})

const operationOffices = computed(() => {
  if (form.operationOffice) {
    return [form.operationOffice]
  }
  return null
})

onMounted(() => {
  const savedFormValues = loadDataFromSessionStorage('every24hoursReportSearchForm');
  if (savedFormValues) {
    form.managementOffice = savedFormValues.managementOffice;
    form.operationOffice = savedFormValues.operationOffice;
    form.group = savedFormValues.group;
    form.department = savedFormValues.department;
    form.vehicle = savedFormValues.vehicle;
    form.driver = savedFormValues.driver;
    form.selectDateType = savedFormValues.selectDateType;
    form.dateRange = [new Date(savedFormValues.dateRange[0]), new Date(savedFormValues.dateRange[1])];
  }
})

function handleClickSearch() {
  if (validateForm()) {
    emits('search', form);
    saveDataToSessionStorage('every24hoursReportSearchForm', form);
  }
}

function clearForm() {
  form.managementOffice = managementOfficeClearable.value ? undefined : form.managementOffice
  form.operationOffice = operationOfficeClearable.value ? undefined : form.operationOffice
  form.group = undefined
  form.department = undefined
  form.vehicle = undefined
  form.driver = undefined
  form.selectDateType = 'leaving'
  form.dateRange = [
    new Date(),
    new Date()
  ]
  clearSessionStorage('every24hoursReportSearchForm')
  emits('clear')
}

function toggleSidebar() {
  hide.value = !hide.value;
}

function validateForm() {
  errors.value = {};
  let ret = true;
  if (!form.operationOffice) {
    errors.value['operationOffice'] = [i18n.global.t('pages.common.filter.errorMssages.pleaseSelect')];
    ret = false;
  }
  if (
    !form.dateRange[0] ||
    !form.dateRange[1] ||
    getDatetimeWithoutSeconds(form.dateRange[0]) > getDatetimeWithoutSeconds(form.dateRange[1])
  ) {
    errors.value['end_date'] = [i18n.global.t('pages.common.filter.errorMssages.pleaseSelectCorrectRange')];
    ret = false;
  } else if (
    getDateRangeDays(form.dateRange[0], form.dateRange[1]) > MAX_FILTER_DAYS
  ) {
    errors.value['end_date'] = [
      `${i18n.global.t('pages.common.filter.errorMssages.range')}${MAX_FILTER_DAYS}${i18n.global.t('pages.common.filter.errorMssages.pleaseSelectWithinDays')}`
    ];
    ret = false;
  }
  return ret;
}

function makeInitialForms() {
  const savedFormValues = loadDataFromSessionStorage('every24hoursReportSearchForm');
  if (savedFormValues) {
    return;
  }
  form.dateRange = [new Date(), new Date()];
  if (props.isOperationOffice || props.isDepartment) {
    return form.dateRange[0].setDate(form.dateRange[0].getDate() - props.defaultBackDays);
  }
}

watch(() => props.isOperationOffice, () => {
  makeInitialForms()
})

watch(() => props.isDepartment, () => {
  makeInitialForms()
})
</script>
<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      {{ title }}
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="hide">
          <path
            d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z"
            fill-rule="evenodd"
            sandboxuid="0"
          ></path>
        </template>
        <template v-if="!hide">
          <path
            d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z"
            sandboxuid="0"
          />
        </template>
      </svg>
    </h4>
    <div :class="`strpied-tabled-with-hover ${hide ? 'd-none' : ''}`">
      <SearchFormContent
        v-model="form"
        :errors="errors"
        :operationOffices="operationOffices"
        :managementOfficeClearable="managementOfficeClearable"
        :operationOfficeClearable="operationOfficeClearable"
        :isDepartment="props.isDepartment"
        :maxFilterDays="MAX_FILTER_DAYS"
      />
      <div class="d-flex flex-column">
        <div class="col-md-3">
          <svg
            version="1.2"
            style="overflow: visible; width: 299px; height: 1px; opacity: 1; mix-blend-mode: normal; rotate(0deg);"
          >
            <defs>
              <path
                id="path-1650848226989195"
                d="M1126 703 C1126 703 1425 703 1425 703"
              ></path>
            </defs>
            <g transform="translate(-1126, -703)">
              <path
                style="
                  stroke: rgb(194, 186, 186);
                  stroke-width: 1;
                  stroke-linecap: butt;
                  stroke-linejoin: miter;
                  fill: none;
                "
                d="M1126 703 C1126 703 1425 703 1425 703"
              ></path>
            </g>
          </svg>
        </div>
        <div class="d-flex search-buttons m-auto">
          <v-btn
            class="mr-5 ma-2 exec-btn"
            @click="handleClickSearch"
            :style="{
              backgroundColor: loading ? '#B3B8BB' : '',
            }"
          >
            {{ searchBtnLabel }}
          </v-btn>
          <v-btn class="ma-2 reset-btn" @click="clearForm">
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
.search-area {
  max-width: 335px;
  width: 45% !important;
  border-left: solid 1px #b3b8bb;
  background-color: white;
}
.search-area .dp__input_reg {
  font-size: 12px;
}
.dp__action_buttons {
  display: flex;
  width: 80%;
  text-align: right;
}
</style>
